/**
 * Invitation Service
 *
 * Handles customer portal invitation verification and registration.
 * Connects to backend API for:
 * - Token validation via POST /invitations/verify
 * - Customer registration via POST /auth/register
 * - Key exchange status polling via GET /auth/key-exchange-status
 * - DEK retrieval via GET /auth/dek
 */

import { apiService } from './api.service'
import type { CustomerWrappedDEK } from '@/types/crypto'
import { toISOStringWithMillis } from '@/utils/dateUtils'

// ============================================
// Types
// ============================================

export type InvitationValidationStatus = 'valid' | 'invalid' | 'expired' | 'used' | 'revoked'

export interface InvitationData {
  consultantName: string
  consultantEmail?: string
  customerEmail?: string
  customerId: string // Internal database ID (e.g., "cust_abc123")
  accessCode: string // Simple login code for Cognito username (e.g., "MK7X9P2Q")
  expiresAt: string
}

export interface ValidateInvitationResponse {
  status: InvitationValidationStatus
  data?: InvitationData
}

export interface RegisterCustomerRequest {
  invitationId: string
  token: string
  accessCode: string // Cognito username (simple 8-char code)
  passphrase: string // Cognito password (also used for key derivation)
  publicKey: string // X25519 public key for key exchange
  salt: string // Salt for key derivation (Base64)
  email?: string // Optional: only stored encrypted in DynamoDB, NOT in Cognito
  referralCode?: string // Optional: referral code from URL for loyalty attribution
}

export interface RegisterCustomerResponse {
  customerId: string
  status: 'registered'
}

export type KeyExchangeStatus = 'pending' | 'registered' | 'active' | 'revoked'

export interface KeyExchangeStatusResponse {
  status: KeyExchangeStatus
  wrappedDEK?: string // Present when status is 'active'
  consultantPublicKey?: string // Consultant's static public key for ECIES
}

// ============================================
// Service Implementation
// ============================================

class InvitationService {
  /**
   * Validate invitation token with backend
   *
   * Called when customer opens invitation link.
   * Backend validates:
   * - Invitation exists for given ID
   * - Token matches stored invitationToken (HMAC verification)
   * - Not expired (tokenExpiresAt > now)
   * - Status is 'pending' (not used/revoked)
   *
   * @param invitationId - Invitation ID from URL query param 'id'
   * @param token - Invitation token from URL query param 'token'
   * @returns Validation result with consultant info if valid
   */
  async validateInvitation(
    invitationId: string,
    token: string
  ): Promise<ValidateInvitationResponse> {
    try {
      const response = await apiService.post<{
        isValid: boolean
        consultantName?: string
        consultantEmail?: string
        customerEmail?: string
        customerId?: string
        accessCode?: string // Simple login code for customer app (Cognito username)
        expiresAt?: string
        reason?: 'expired' | 'used' | 'revoked' | 'invalid_token' | 'not_found'
      }>(
        '/invitations/verify',
        { invitationId, token },
        { skipAuth: true } // No auth needed for invitation verification
      )

      if (response.isValid && response.customerId && response.accessCode) {
        return {
          status: 'valid',
          data: {
            consultantName: response.consultantName || 'Your Consultant',
            consultantEmail: response.consultantEmail,
            customerEmail: response.customerEmail,
            customerId: response.customerId,
            accessCode: response.accessCode,
            expiresAt: response.expiresAt || toISOStringWithMillis(new Date(Date.now() + 72 * 60 * 60 * 1000)),
          },
        }
      }

      // Map backend reason to status
      const reasonToStatus: Record<string, InvitationValidationStatus> = {
        expired: 'expired',
        used: 'used',
        revoked: 'revoked',
        invalid_token: 'invalid',
        not_found: 'invalid',
      }

      return {
        status: response.reason ? reasonToStatus[response.reason] || 'invalid' : 'invalid',
      }
    } catch (error) {
      console.error('Failed to validate invitation:', error)
      return { status: 'invalid' }
    }
  }

  /**
   * Register customer with public key (Zero-Knowledge)
   *
   * Called after customer creates passphrase and derives key pair.
   * Sends:
   * - invitationId and token for verification
   * - accessCode (Cognito username - simple 8-char code)
   * - passphrase (Cognito password - also used for key derivation)
   * - Customer's X25519 public key (Base64)
   * - Salt used for key derivation (Base64)
   * - email (optional - only stored encrypted in DynamoDB, NOT in Cognito)
   *
   * Backend will:
   * - Create Cognito user with accessCode + passphrase (NO email in Cognito)
   * - Store customer's public key
   * - Store encrypted email in DynamoDB (if provided)
   * - Notify consultant that key exchange is pending
   *
   * @param data - Registration data with public key
   * @returns Customer ID and registration status
   */
  async register(data: RegisterCustomerRequest): Promise<RegisterCustomerResponse> {
    const response = await apiService.post<RegisterCustomerResponse>(
      '/auth/register',
      {
        invitationId: data.invitationId,
        invitationToken: data.token,
        accessCode: data.accessCode,
        passphrase: data.passphrase,
        customerPublicKey: data.publicKey,
        keySalt: data.salt,
        email: data.email, // Optional - only stored encrypted, NOT in Cognito
        ...(data.referralCode ? { referralCode: data.referralCode } : {}),
      },
      { skipAuth: true } // No auth needed for registration
    )

    return response
  }

  /**
   * Get key exchange status
   *
   * Polls backend to check if consultant has completed key exchange.
   * Called repeatedly from WaitingForConsultant page.
   *
   * Status progression:
   * - 'pending': Invitation created, customer not registered
   * - 'registered': Customer registered, waiting for consultant
   * - 'active': Consultant wrapped DEK, customer can proceed
   * - 'revoked': Access revoked by consultant
   *
   * @returns Current key exchange status and wrapped DEK if active
   */
  async getKeyExchangeStatus(): Promise<KeyExchangeStatusResponse> {
    const response = await apiService.get<KeyExchangeStatusResponse>('/auth/key-exchange-status')
    return response
  }

  /**
   * Get wrapped DEK for unlocking encryption
   *
   * Called during unlock to fetch the ECIES-wrapped DEK and salt.
   * The customer uses this to derive their key pair and unwrap the DEK.
   *
   * Prerequisites:
   * - Customer must be authenticated
   * - Key exchange must be complete (status: 'active')
   *
   * @returns Wrapped DEK, salt, and version
   * @throws ApiError if key exchange not complete or DEK not available
   */
  async getDEK(): Promise<{
    wrappedDEK: CustomerWrappedDEK
    salt: string
    version: number
  }> {
    const response = await apiService.get<{
      wrappedDEK: CustomerWrappedDEK
      salt: string
      version: number
    }>('/auth/dek')
    return response
  }
}

export const invitationService = new InvitationService()
export default invitationService
